import type { JSX } from 'react';

import { Translate, TranslateHtml } from '@change-corgi/core/react/i18n';
import { Card, Separator } from '@change-corgi/design-system-legacy/components/content';
import { Avatar } from '@change-corgi/design-system-legacy/components/media';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { Heading, Text } from '@change-corgi/design-system-legacy/typography';

import { InfoTooltip } from 'src/app/shared/components/tooltips';

import { useDashboardReskinExperiment } from '../../../../../../../../hooks/useDashboardReskinExperiment';
import { ExportButton } from '../../../../../shared/ExportButton';
import { useExportButton } from '../../../../../shared/ExportButton/hooks/useExportButton';
import { ExportNotifications } from '../../../../../StarterDashboardComments/components/ExportNotifications';

import { useRecentSupportersList } from './hooks/useRecentSupportersList';
import { RecentSupportersList as RecentSupportersListNew } from './index_new';

export function RecentSupportersList(): JSX.Element {
	const {
		data: { isEnabled },
	} = useDashboardReskinExperiment();

	const { data, actions } = useRecentSupportersList();
	const { data: exportData, actions: exportActions } = useExportButton('signatures');

	if (isEnabled) {
		return <RecentSupportersListNew />;
	}

	return (
		<Card
			as="section"
			backgroundColor="white"
			sx={{ border: 'unset', display: 'flex', flexDirection: 'column', borderRadius: 'big', position: 'relative' }}
			data-qa="starter-dashboard-recent-supporters-card"
			data-testid="starter-dashboard-recent-supporters-card"
			aria-label="export-recent-supporters-card"
			aria-describedby="export-recent-supporters-card"
		>
			<Flex p="24px 24px 16px" sx={{ alignItems: 'center', gap: 4, height: '84px' }}>
				<Heading as="h3" size="h4" id="recent-supporters-title">
					<Translate value="corgi.petition.starter_dashboard.engage_your_supporters.recent_supporters.title" />
				</Heading>
				<InfoTooltip aria-describedby="recent-supporters-title">
					<Translate value="corgi.petition.starter_dashboard.engage_your_supporters.recent_supporters.tip" />
				</InfoTooltip>
				<Box sx={{ ml: 'auto', justifySelf: 'end' }}>
					<ExportButton
						exportType="SIGNATURES"
						data-testid="export-recent-supporters-button"
						data-qa="export-recent-supporters-button"
						aria-label="export-recent-supporters-button"
						aria-describedby="export-recent-supporters-button"
						data={exportData}
						actions={exportActions}
					/>
				</Box>
			</Flex>
			<Separator />
			<Box
				as="ul"
				role="list"
				sx={{
					minHeight: '357px',
					height: '357px',
					overflow: 'auto',
					flex: '1 1 auto',
					backdropFilter: 'blur(8px)',
					WebkitBackdropFilter: 'blur(8px)',
					maskImage: 'linear-gradient(to top, transparent 0%, black 30%)',
					WebkitMaskImage: 'linear-gradient(to top, transparent 0%, black 30%)',
				}}
				onScroll={actions.onScrollSupporters}
				data-testid="recent-supporters-list"
			>
				{data.recentSupporters.map((supporter) => (
					<Flex
						as="li"
						role="listitem"
						px={24}
						py={6}
						sx={{ alignItems: 'center', minHeight: '48px', width: '100%' }}
						key={supporter.uniqueKey}
					>
						<Avatar alt="avatar" size="xs" src={supporter.photo?.url} variant="subtle" />
						<Text as="p" ml="8px">
							<Text as="span">
								<TranslateHtml
									value="corgi.petition.starter_dashboard.engage_your_supporters.recent_supporters.name_with_sign_time"
									replacements={{
										name: supporter.displayName,
										relative_time: supporter.relativeTimestamp,
									}}
								/>
							</Text>
						</Text>
					</Flex>
				))}
			</Box>
			<ExportNotifications data={exportData} />
		</Card>
	);
}
